import React, { useState, useRef } from 'react';
import { Document as DocumentViewer, Page, pdfjs } from 'react-pdf';
import Slider from '@mui/material/Slider';
import { DocContainer } from 'pages/home/Content.style';
import CloseIcon from './CloseIcon';

import { ReactComponent as Leftarrow } from 'assert/images/Common/arrowBackwardCircle.svg';
import { ReactComponent as Rightarrow } from 'assert/images/Common/arrowForwardCircle.svg';
import { ReactComponent as Download } from 'assert/images/Common/download.svg';
import { ReactComponent as Document } from 'assert/images/Common/document.svg';
import { Skeleton } from '@mui/material';
import styled from 'styled-components';

const CenteredLogo = styled(Document)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  path {
    stroke: #5f5f5f;
  }
`;

const DocPreview = ({
  src,
  onDelete,
  readOnly,
  isScroll = false,
  isScrollDownload,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [isLoaded, setLoaded] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const containerRef = useRef(null);
  const [viewerHeight, setViewerHeight] = useState(0);
  const preloadedPages = useRef(new Set());
  const pdfDocumentRef = useRef(null);

  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoaded(true);

    const container = containerRef.current;
    if (container) {
      const pageElement = container.querySelector('.react-pdf__Page__canvas');
      if (pageElement) {
        setViewerHeight(pageElement.offsetHeight);
      }
    }

    preloadPages(pageNumber, numPages);
  };

  const preloadPages = (currentPage, totalPages) => {
    if (!pdfDocumentRef.current) return;

    const pagesToPreload = [];
    for (let i = -3; i <= 3; i++) {
      const page = currentPage + i;
      if (page > 0 && page <= totalPages && !preloadedPages.current.has(page)) {
        pagesToPreload.push(page);
      }
    }

    pagesToPreload.forEach((page) => {
      pdfDocumentRef.current.getPage(page).then(() => {
        preloadedPages.current.add(page);
      });
    });
  };

  const changePage = (newPage) => {
    setPageNumber(newPage);
    preloadPages(newPage, numPages);
  };

  const previousPage = () => changePage(Math.max(pageNumber - 1, 1));
  const nextPage = () => changePage(Math.min(pageNumber + 1, numPages));

  const onDownloadFile = () => {
    const link = document.createElement('a');
    link.href = src;
    link.setAttribute('download', 'filename.pdf');
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DocContainer
      id="DocContainer"
      ref={containerRef}
      readonly={!readOnly}
      isscroll={isScroll}
    >
      <div
        style={{
          height: isScroll ? 'auto' : viewerHeight || 'auto',
          overflow: isScroll ? 'auto' : 'hidden',
          position: 'relative',
        }}
      >
        <DocumentViewer
          file={src}
          onLoadSuccess={(doc) => {
            onDocumentLoadSuccess(doc);
            pdfDocumentRef.current = doc;
          }}
          loading={
            <>
              <Skeleton
                variant="rectangular"
                height={500}
                width={'100%'}
                animation="wave"
              />
              <CenteredLogo />
            </>
          }
        >
          {isScroll && numPages ? (
            Array.from({ length: numPages }, (_, i) => (
              <div className="pdf-page-wrapper" key={i + 1}>
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  width={containerRef.current?.offsetWidth || undefined}
                />
              </div>
            ))
          ) : (
            <Page
              pageNumber={pageNumber}
              width={containerRef.current?.offsetWidth || undefined}
              onRenderSuccess={() => {
                if (!viewerHeight) {
                  const pageElement = containerRef.current.querySelector(
                    '.react-pdf__Page__canvas'
                  );
                  if (pageElement) {
                    setViewerHeight(pageElement.offsetHeight);
                  }
                }
              }}
            />
          )}
        </DocumentViewer>
      </div>

      {isLoaded && !readOnly && (
        <CloseIcon customStyle="docCloseIcon" onClick={onDelete} />
      )}

      {!isScroll && isLoaded && numPages > 1 && (
        <>
          <button onClick={previousPage} className="document-backbtn">
            <Leftarrow />
          </button>
          <button onClick={nextPage} className="document-nextbtn">
            <Rightarrow />
          </button>
        </>
      )}

      {!isScroll && isLoaded && (
        <div className="page-number-doc">
          <div className="doc-action">
            {pageNumber}/{numPages}
            <div className="slider-container">
              <Slider
                value={pageNumber}
                onChange={(_, value) => changePage(value)}
                marks
                min={1}
                step={1}
                max={numPages}
                aria-label="Page Slider"
                aria-labelledby="page-slider"
              />
            </div>
            <Download onClick={onDownloadFile} />
          </div>
        </div>
      )}
      {isScrollDownload ? (
        <Download onClick={onDownloadFile} className="scroll-download" />
      ) : (
        <></>
      )}
    </DocContainer>
  );
};

export default DocPreview;
